import { keyPicker } from 'factions/metatagger'
import {
  CHARGE_PHASE,
  COMBAT_PHASE,
  DURING_GAME,
  DURING_SETUP,
  END_OF_MOVEMENT_PHASE,
  END_OF_SETUP,
  END_OF_TURN,
  HERO_PHASE,
  MOVEMENT_PHASE,
  SHOOTING_PHASE,
  START_OF_ROUND,
  START_OF_TURN,
} from 'types/phases'
import CommandAbilities from './command_abilities'
import { TItemDescriptions } from 'factions/factionTypes'

const Flavors = {
  'Lords of the Clan': {
    effects: [
      {
        name: `Nature's Titans - Passive`,
        desc: `Effect: When using the 'Endless Growth' ability, do not roll a D3 for Monster targets. Instead, Heal (3) each of those targets.`,
        when: [END_OF_TURN],
      },
    ],
  },
  'Followers of Kurnoth': {
    effects: [
      {
        name: `Sacred Quarry - Once Per Turn`,
        desc: `Declare: Pick a friendly Sylvaneth unit to be the target.
        Effect: Until the start of your next turn, add 1 to hit rolls for the target's attacks while it is wholly within 12" of the enemy general or an enemy Warmaster.`,
        when: [HERO_PHASE],
      },
    ],
  },
  Outcasts: {
    effects: [
      {
        name: `Fear of the Deepwoods - Passive`,
        desc: `Effect: In the 'Creeping Dread' ability, replace 'while it is not wholly within 12" of a Hero in their army' with 'while it is not wholly within 6" of a Hero in their army'.`,
        when: [START_OF_TURN],
      },
    ],
  },
  'Glade Defenders': {
    effects: [
      {
        name: `Memories of the Fallen - Once Per Turn`,
        desc: `Declare: For each friendly Sylvaneth unit or friendly Awakened Wyldwood that was destroyed in the previous turn, you can pick a friendly Sylvaneth unit to be a target. 
        Effect: Add 1 to the Attacks characteristic of each target's melee weapons for the rest of the turn.`,
        when: [HERO_PHASE],
      },
    ],
  },
  'Wargrove of the Burgeoning': {
    effects: [
      {
        name: `Stirring Life - Once Per Turn`,
        desc: `Declare: Pick a friendly non-Hero Sylvaneth unit within 3" of a terrain feature to be the target. 
        Effect: If the target has a Health characteristic of 1, return up to 3 slain models to the target. If the target has a Health characteristic of 2 or more, roll a dice, then pick 1 of the following: 
        Heal (X) the target, where X is equal to the roll. 
        Return a number of slain models to the target with a combined Health characteristic that is equal to or less than the roll.`,
        when: [END_OF_TURN],
      },
    ],
  },
  'Wargrove of Everdusk': {
    effects: [
      {
        name: `Duskwalk - Passive`,
        desc: `Effect: Friendly Sylvaneth Heroes can use the Walk the Hidden Paths ability even if it has been used by another friendly unit in the same phase.`,
        when: [MOVEMENT_PHASE],
      },
    ],
  },
  'The Evergreen Hunt (AoR)': {
    effects: [
      {
        name: `Rhythm of the Chase - Once Per Battle Round`,
        desc: `Declare: If there is no quarry on the battlefield, pick an enemy unit on the battlefield to be the target. 
        Effect: The target is the quarry for the rest of the battle.`,
        when: [START_OF_ROUND],
      },
      {
        name: `Opening Horn Blast - Once Per Battle Round`,
        desc: `Declare: You must use this ability at the start of the battle round. 
        Effect: Your chords are reset to 0. Then, you gain 1 chord: 
        If a friendly Belthanos is on the battlefield. 
        For each friendly Evergreen Hunt unit that is wholly within the same large quarter of the battlefield as the quarry. 
        For each quarry destroyed so far during the battle.`,
        when: [START_OF_ROUND],
      },
      {
        name: `Song of the Hunt - Passive`,
        desc: `Effect: The following cumulative effects apply to friendly Evergreen Hunt units depending on the number of chords you have: 
        1 chord - Simple: Add 1 to run rolls and charge rolls for friendly units wholly within the same quarter of the battlefield as the quarry. 
        2 chords - Tuneful: Add 1 to hit rolls and wound rolls for friendly units combat attacks that target an enemy unit wholly within the same quarter of the battlefield as the quarry. 
        3-5 chords - Melodic: Add 1 to the Attacks characteristic of melee weapons used by friendly units while they are in combat with the quarry. 
        6+ chords - Mellifluous: While a friendly unit is in combat with the quarry, the quarry has Strike-last.`,
        when: [DURING_GAME],
      },
      {
        name: `Command Ability: A Prize Quarry is Sighted - Once Per Battle`,
        desc: `Declare: Pick an enemy unit within 9" of a friendly Evergreen Hunt unit to be the target. 
        Effect: The target becomes the quarry for the rest of the battle. If there was already a quarry on the battlefield when you declared this ability, that unit is no longer the quarry.`,
        when: [HERO_PHASE],
      },
      {
        name: `Abundant Growth - Once Per Turn`,
        desc: `Declare: Pick each friendly Evergreen Hunt unit that is wholly within 3" of a terrain feature to be the targets. 
        Effect: Heal (1) each target.`,
        when: [HERO_PHASE],
      },
      {
        name: `Merciful Strike - Once Per Turn`,
        desc: `Declare: Pick a friendly Evergreen Hunt Monster that has not used a Rampage ability this turn to use this ability, then pick an enemy unit in combat with it to be the target. 
        Effect: Roll a dice and add the number of damage points the target has. If the result exceeds the targets Health characteristic, 1 model in the target unit is slain.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'Lords of the Clan (AoR)': {
    effects: [
      {
        name: `Dormant Heroes`,
        desc: `Declare: If there are more friendly Lords of the Clan units on the battlefield than there are in reserve, pick a friendly Lords of the Clan Monster unit that has not been deployed.
        Effect: Set up that unit in reserve as a dormant forest spirit. It has now been deployed.`,
        when: [DURING_SETUP],
      },
      {
        name: `Elder Authority - Once Per Turn`,
        desc: `Declare: You can only use this ability if there is a friendly Awakened Wyldwood on the battlefield.
        Effect: You can set up up to 2 additional Awakened Wyldwoods on the battlefield (an Awakened Wyldwood is a single tree miniature with a crescent-shaped base). The first can be set up anywhere on the battlefield more than 3" from all units, objectives and other terrain features. The second can be set up anywhere on the battlefield wholly outside of enemy territory and more than 3" from all units, objectives and other terrain features.`,
        when: [DURING_SETUP],
      },
      {
        name: `Our Reach is Long - Passive`,
        desc: `Effect: The creeping overgrowth (referenced by other abilities) extends 6" from each terrain feature that has a friendly overgrown token.
        Increase this range by 1" for each friendly Lords of the Clan unit that is wholly outside friendly territory.`,
        when: [DURING_GAME],
      },
      {
        name: `Command Ability: Roused to Fury - Enemy Movement Phase`,
        desc: `Declare: Pick an enemy unit within 9" of a friendly Awakened Wyldwood to be the target.
        Effect: Pick 1 of the following units:
        A friendly Lords of the Clan unit that is in reserve as a dormant forest spirit.
        A friendly Lords of the Clan Monster that has been destroyed.
        Set up the unit you picked, or a replacement unit if you picked a destroyed unit, within 9" of the target, wholly within 6" of that friendly Awakened Wyldwood and not in combat. If you set up a replacement unit, allocate 6 damage points to it (ward rolls cannot be made for those damage points).
        Then, remove that friendly Awakened Wyldwood from the battlefield.`,
        when: [MOVEMENT_PHASE],
      },
    ],
  },
  'Soulpod Guardians (AoR)': {
    effects: [
      {
        name: `The Sacred Groves - Once Per Battle`,
        desc: `Declare: You must use this ability.
        Effect: Set up 3 Awakened Wyldwoods (an Awakened Wyldwood is a single tree miniature with a crescent-shaped base) wholly within friendly territory, more than 1" from all other terrain features and more than 3" from all objectives. The tips of each crescent-shaped base must touch, forming a circle.
        Once you have done so, for the rest of the battle, the 3 tree miniatures join to form 1 Soulpod Grove terrain feature. The area inside the circle formed by the trees is part of the terrain feature.`,
        when: [DURING_SETUP],
      },
      {
        name: `Jade Haven - Passive`,
        desc: `Effect: The Health characteristic of your Soulpod Grove is 18. The creeping overgrowth extends 12" from your Soulpod Grove.`,
        when: [DURING_GAME],
      },
      {
        name: `Vengeance for the Lost - Passive`,
        desc: `Effect: If your Soulpod Grove has been destroyed this battle, add 2 to the Attacks characteristic of friendly Soulpod Guardians units' melee weapons.`,
        when: [COMBAT_PHASE],
      },
      {
        name: `Heart of the Forest - Passive`,
        desc: `Effect: You cannot allocate more than 10 damage points per phase to a friendly Soulpod Guardians unit while it is wholly within 12" of your Soulpod Grove (remaining damage points inflicted on it in that phase have no effect).`,
        when: [DURING_GAME],
      },
    ],
  },


  // Glades
  /* Oakenbrow: {
    effects: [
      {
        name: `Our Roots Run Deep`,
        desc: `When determining which row to use on the damage table of a friendly OAKENBROW TREELORD, TREELORD ANCIENT or SPIRIT OF DURTHU, it is treated as having suffered half the number of wounds that are actually allocated to it (rounding up).`,
        when: [DURING_GAME, COMBAT_PHASE],
      },
    ],
  },
  Gnarlroot: {
    effects: [
      {
        name: `Keepers of the Arcane`,
        desc: `Once per turn, when you make a casting roll or unbinding roll for a friendly GNARLROOT WIZARD that is wholly within 9" of an overgrown terrain feature or friendly Awakened Wyldwood, you can roll 3D6 instead of 2D6. If you do so, remove 1 dice of your choice from the roll, and then use the remaining dice to determine that casting roll or unbinding roll.`,
        when: [HERO_PHASE],
      },
    ],
  },
  Heartwood: {
    effects: [
      {
        name: `Masters of the Hunt`,
        desc: `After deployment but before the first battle round begins, you can pick up to 3 different enemy units on the battlefield to be the quarry of the hunt. If you do so, add 1 to hit rolls for attacks made by friendly HEARTWOOD units that target those units.`,
        when: [DURING_SETUP, COMBAT_PHASE, SHOOTING_PHASE],
      },
    ],
  }, */
  /* Ironbark: {
    /*mandatory: {
      command_abilities: [keyPicker(CommandAbilities, ['Stand Firm'])],
    }, 
    effects: [],
  },
  Winterleaf: {
    effects: [
      {
        name: `Winters Bite`,
        desc: `Enemy units within 3" of a friendly WINTERLEAF unit cannot retreat. In addition, if you pick Everdusk from the Seasons of War battle trait, enemy units within 3" of a friendly WINTERLEAF unit cannot be removed from the battlefield through an effect that would allow them to be set up again later in the battle.`,
        when: [DURING_GAME, MOVEMENT_PHASE],
      },
    ],
  },
  Dreadwood: {
    effects: [
      {
        name: `Malicious Tormentors`,
        desc: `Once per battle, you can use Walk the Hidden Paths twice in the same turn, but if you do so, at least one of the units you pick must be a friendly DEADWOOD SPITE-REVENANTS unit. In addition, once per battle, you can use Strike and Fade twice in the same turn, but if you do so, at least one of the units you pick must be a friendly DREADWOOD SPITE-REVENANTS unit.`,
        when: [END_OF_MOVEMENT_PHASE, COMBAT_PHASE],
      },
    ],
  },
  Harvestboon: {
    effects: [
      {
        name: `Vibrant Surge`,
        desc: `After deployment but before the first battle round begins, you can move each friendly HARVESTBOON SPITERIDER LANCERS and REVENANT SEEKERS unit up to 12". If both players can move units before the first battle round begins, they must roll off and the winner chooses who moves their units first.`,
        when: [END_OF_SETUP],
      },
    ],
  }, */
} satisfies TItemDescriptions

export default Flavors
